import { FC, ImgHTMLAttributes, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useImageUrl, UseImageUrlBaseProps } from '../../hooks/useImageUrl';
import { useImagePathConfig } from '../../contexts/ImagePathContext';

export interface CountryFlagProps extends Partial<UseImageUrlBaseProps> {
  className?: string;
  country: string;
  fallbackCountry?: string;
  loading?: ImgHTMLAttributes<HTMLImageElement>['loading'];
  decoding?: ImgHTMLAttributes<HTMLImageElement>['decoding'];
}

export const CountryFlag: FC<CountryFlagProps> = ({
  className,
  country,
  fallbackCountry = 'WW',
  hostname = 'https://engagecraftfantasydev.blob.core.windows.net',
  pathname: pathnameProp,
  ext = 'png',
  loading = 'lazy',
  decoding = 'async',
}) => {
  const imagePaths = useImagePathConfig();
  const pathname = pathnameProp ?? imagePaths?.countryFlagPath ?? 'country-flags';
  const normalizedCountry = country.trim();
  const normalizedFallbackCountry = fallbackCountry.trim();
  const [showPlaceholder, setShowPlaceholder] = useState(
    !normalizedCountry && !normalizedFallbackCountry
  );
  const { url, value, onError } = useImageUrl({
    value: normalizedCountry || normalizedFallbackCountry,
    fallbackValue: normalizedFallbackCountry || normalizedCountry,
    hostname,
    pathname,
    ext,
  });

  useEffect(() => {
    setShowPlaceholder(!normalizedCountry && !normalizedFallbackCountry);
  }, [normalizedCountry, normalizedFallbackCountry]);

  const handleError = () => {
    if (!normalizedFallbackCountry || value === normalizedFallbackCountry) {
      setShowPlaceholder(true);
      return;
    }

    onError();
  };

  if (showPlaceholder) {
    return (
      <span
        role="img"
        aria-label="Flag unavailable"
        className={clsx(
          'inline-flex items-center justify-center bg-[#eae9fd] text-[10px] font-bold text-[#757777] rounded-full overflow-hidden',
          className
        )}
      >
        ?
      </span>
    );
  }

  return (
    <picture>
      <img
        className={clsx(className)}
        src={url.toString()}
        alt={value}
        loading={loading}
        decoding={decoding}
        onError={handleError}
      />
    </picture>
  );
};
